import clsx from "clsx";
import type { PlanScore } from "@yb/core";

export function QualityRing({ value, size = 44, label = true }: { value: number; size?: number; label?: boolean }) {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const color = value >= 85 ? "var(--ok)" : value >= 65 ? "var(--warn)" : "var(--bad)";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={4} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={4}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - value / 100)}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 600ms ease, stroke 600ms" }}
        />
      </svg>
      {label && (
        <div className="tabular absolute inset-0 grid place-items-center font-bold" style={{ fontSize: size * 0.32 }}>
          {value}
        </div>
      )}
    </div>
  );
}

export function Kpi({
  label,
  value,
  sub,
  tone = "neutral",
  delta,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: "neutral" | "bad" | "warn" | "ok";
  delta?: { value: number; better: "up" | "down"; suffix?: string } | null;
}) {
  const good = delta && (delta.better === "up" ? delta.value > 0 : delta.value < 0);
  return (
    <div className="min-w-0">
      <div className="truncate text-[10.5px] font-medium tracking-wide text-ink-3 uppercase">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span
          className={clsx(
            "tabular text-[20px] leading-7 font-bold",
            tone === "bad" && "text-bad",
            tone === "warn" && "text-warn",
            tone === "ok" && "text-ok",
          )}
        >
          {value}
        </span>
        {delta && delta.value !== 0 && (
          <span className={clsx("tabular text-[12px] font-bold", good ? "text-ok" : "text-bad")}>
            {delta.value > 0 ? "+" : ""}
            {delta.value}
            {delta.suffix ?? ""}
          </span>
        )}
        {sub && <span className="truncate text-[11px] text-ink-3">{sub}</span>}
      </div>
    </div>
  );
}

export function ScoreKpis({ score, predicted, compare }: { score: PlanScore; predicted?: number; compare?: PlanScore }) {
  const d = (a: number, b: number | undefined) => (b === undefined ? null : a - b);
  return (
    <>
      <Kpi
        label="Hard conflicts"
        value={score.hardConflicts + score.unallocated}
        tone={score.hardConflicts + score.unallocated > 0 ? "bad" : "ok"}
        delta={compare ? { value: d(score.hardConflicts + score.unallocated, compare.hardConflicts + compare.unallocated)!, better: "down" } : null}
      />
      {predicted !== undefined && (
        <Kpi label="Predicted · next 4 h" value={predicted} tone={predicted > 0 ? "warn" : "neutral"} />
      )}
      <Kpi
        label="Jet bridge pax"
        value={`${score.jetbridgePaxPct}%`}
        delta={compare ? { value: d(score.jetbridgePaxPct, compare.jetbridgePaxPct)!, better: "up", suffix: "pt" } : null}
      />
      <Kpi
        label="Airline preferences"
        value={`${score.preferenceMet}/${score.preferenceTotal}`}
        delta={compare ? { value: d(score.preferenceMet, compare.preferenceMet)!, better: "up" } : null}
      />
      <Kpi label="Tightest gap" value={score.minBufferMin === null ? "—" : `${score.minBufferMin}m`} sub={`${score.tightTurns} tight`} />
    </>
  );
}
